import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils/ui";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as React from "react";

type VirtualizedLogTableColumn<TData> = {
  key: string;
  header: React.ReactNode;
  cell: (row: TData) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  width?: string;
};

type VirtualizedLogTableProps<TData> = {
  data: TData[];
  columns: VirtualizedLogTableColumn<TData>[];
  getRowKey: (row: TData) => string;
  emptyText?: string;
  className?: string;
  containerClassName?: string;
  rowClassName?: string;
  estimateRowHeight?: number;
  overscan?: number;
  minWidth?: string;
  maxHeight?: string;
};

export function VirtualizedLogTable<TData>({
  data,
  columns,
  getRowKey,
  emptyText = "No logs",
  className,
  containerClassName,
  rowClassName,
  estimateRowHeight = 56,
  overscan = 8,
  minWidth = "100%",
  maxHeight = "min(34rem, calc(100svh - 20rem))",
}: VirtualizedLogTableProps<TData>) {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const gridTemplateColumns = columns
    .map((column) => column.width ?? "minmax(0, 1fr)")
    .join(" ");

  const rowVirtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateRowHeight,
    overscan,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();

  return (
    <Card className={cn("overflow-hidden", className)}>
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          <div
            className="grid h-16 items-center border-b bg-secondary font-semibold"
            style={{ gridTemplateColumns }}
          >
            {columns.map((column) => (
              <div
                key={column.key}
                className={cn("text-center", column.headerClassName)}
              >
                {column.header}
              </div>
            ))}
          </div>

          {data.length === 0 ? (
            <div className="flex h-20 items-center justify-center text-center">
              {emptyText}
            </div>
          ) : (
            <div
              ref={parentRef}
              className={cn("overflow-y-auto", containerClassName)}
              style={{ maxHeight }}
            >
              <div
                className="relative"
                style={{ height: rowVirtualizer.getTotalSize() }}
              >
                {virtualRows.map((virtualRow) => {
                  const row = data[virtualRow.index];

                  return (
                    <div
                      key={getRowKey(row)}
                      className={cn(
                        "absolute left-0 grid w-full items-center border-b",
                        rowClassName,
                      )}
                      style={{
                        gridTemplateColumns,
                        height: virtualRow.size,
                        transform: `translateY(${virtualRow.start}px)`,
                      }}
                    >
                      {columns.map((column) => (
                        <div
                          key={column.key}
                          className={cn("text-center", column.className)}
                        >
                          {column.cell(row)}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
